import type { ApiResponse } from '@evi/shared';
import { api } from './api';
import { newClientId, type CapturedPhoto } from './photoCapture';
import { uploadPhoto } from './partnerQuotes';

/**
 * Job-site mode's client (Api: routes/admin/partner-jobs.ts, /site). Install day happens in parkades and basements, so
 * every photo and every step goes into a queue on the phone first (IndexedDB) and uploads when the phone has signal.
 * Each item carries its own id, so a retry is recorded once. The last view of the job is kept on the phone too, so the
 * page reopens without signal.
 */
export type StepKind = 'photo' | 'scan' | 'wifi' | 'plugin' | 'label' | 'golive' | 'signoff';
/** deferred: the step's charger was marked "Installed, offline"; it waits for the connection, not for the crew. */
export interface SiteStep { key: string; kind: StepKind; title: string; hint: string; slot: string | null; done: boolean; done_at: string | null; photos: number; deferred?: boolean }
/** A charger's plug-in and label tests per connector (statuses only; launch reads the same evidence). */
export interface UnitTestConnector {
  id: number; status: string; testable: boolean;
  plug_in: 'passed' | 'not_seen' | 'open'; plug_in_at: string | null; watching_since: string | null;
  label: 'passed' | 'open'; label_at: string | null; label_open_until: string | null;
}
export interface UnitTests { ready: boolean; tariff_set: boolean; tariff_note: string | null; testing_open: boolean; launched: boolean; connectors: UnitTestConnector[]; plugin_done: boolean; label_done: boolean }
export interface SiteUnit {
  slot: string; label: string; identity: string | null; serial: string | null; boot_serial: string | null; matched: boolean | null;
  online: boolean; last_heartbeat: string | null; last_boot: string | null;
  /** "Installed, offline" (Warren, 21 October 2026): when the crew marked it, until it passes its final test. */
  installed_offline: { at: string } | null;
  connectors: Array<{ id: number; status: string; qr_url: string | null }>; endpoint: string | null; tests: UnitTests | null;
  final_test: { at: string; passed: boolean; problems: string[]; checks: Record<string, boolean | null>; differing_keys: Array<{ key: string; expected: string; found: string }> } | null;
}
export interface SitePhoto { id: string; step_key: string | null; charger_identity: string | null; taken_at: string | null; location: { lat: number; lng: number } | null; caption: string; by: string; client_id: string | null }
export interface JobSiteView {
  job: { id: string; title: string; address: string; self_sourced: boolean; status: string }; can_work: boolean;
  /** Where the chargers are and how they reach EVI: site_wifi shows the WiFi steps, kit the connectivity kit's. Never a password. */
  connectivity?: { placement: string | null; building_wifi: boolean | null; site_wifi: boolean; kit: boolean };
  progress: { done: number; total: number }; steps: SiteStep[]; photos: SitePhoto[]; units: SiteUnit[]; signoff: { name: string; at: string; offline?: string[] } | null;
}

const VIEW_KEY = (jobId: string) => `evi.jobsite.${jobId}`;
export async function getJobSite(jobId: string): Promise<{ view: JobSiteView; offline: boolean }> {
  try {
    const r = await api.get(`admin/partner-jobs/${jobId}/site`, { timeout: 15_000, retry: 0 }).json<ApiResponse<JobSiteView>>();
    try { localStorage.setItem(VIEW_KEY(jobId), JSON.stringify(r.data)); } catch { /* storage full or blocked: the page still works online */ }
    return { view: r.data!, offline: false };
  } catch (err) {
    const status = (err as { response?: Response }).response?.status;
    if (status && status < 500) throw err;
    const saved = (() => { try { return localStorage.getItem(VIEW_KEY(jobId)); } catch { return null; } })();
    if (saved) return { view: JSON.parse(saved) as JobSiteView, offline: true };
    throw new Error('No signal, and this job was not opened on this phone before. Open it once with signal.');
  }
}

// ---- The queue on the phone -----------------------------------------------------------------------------------------
type Queued =
  | { id: string; jobId: string; kind: 'photo'; created_at: string; photo: Omit<CapturedPhoto, 'blob'> & { blob: Blob }; attach: { step_key: string; charger_identity?: string; phase: 'install' | 'final' | 'survey'; survey_id?: string } }
  | { id: string; jobId: string; kind: 'action'; created_at: string; path: string; body: Record<string, unknown>; label: string };
const DB = 'evi-jobsite', STORE = 'queue';
function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE, { keyPath: 'id' }); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('This phone cannot keep install data offline.'));
  });
}
async function tx<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const t = d.transaction(STORE, mode), r = run(t.objectStore(STORE));
    r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error);
  });
}
export const queued = async (jobId: string) => ((await tx('readonly', s => s.getAll())) as Queued[]).filter(q => q.jobId === jobId).sort((a, b) => a.created_at.localeCompare(b.created_at));
const put = (q: Queued) => tx('readwrite', s => s.put(q));
const drop = (id: string) => tx('readwrite', s => s.delete(id));

export async function queuePhoto(jobId: string, photo: CapturedPhoto, attach: Extract<Queued, { kind: 'photo' }>['attach']) {
  await put({ id: photo.client_id, jobId, kind: 'photo', created_at: new Date().toISOString(), photo, attach });
}
export async function queueAction(jobId: string, path: string, body: Record<string, unknown>, label: string) {
  const id = newClientId();
  await put({ id, jobId, kind: 'action', created_at: new Date().toISOString(), path, body: { ...body, client_action_id: id, at: new Date().toISOString() }, label });
  return id;
}

let flushing: Promise<{ sent: number; failed: string[] }> | null = null;
/** Uploads what waits on the phone, oldest first: photos before the steps that need them. Network trouble stops the
 * round and keeps the rest; a refusal from the Api is reported and dropped (retrying would not change it). */
export function flush(jobId: string): Promise<{ sent: number; failed: string[] }> {
  flushing ??= (async () => {
    let sent = 0; const failed: string[] = [];
    try {
      for (const q of await queued(jobId)) {
        try {
          if (q.kind === 'photo') await uploadPhoto(q.photo, { phase: q.attach.phase, job_id: q.attach.survey_id ? undefined : jobId, survey_id: q.attach.survey_id, step_key: q.attach.step_key, charger_identity: q.attach.charger_identity });
          else await api.post(q.path, { json: q.body, timeout: 30_000, retry: 0 }).json();
          await drop(q.id); sent++;
        } catch (err) {
          const status = (err as { status?: number; response?: Response }).status ?? (err as { response?: Response }).response?.status ?? 0;
          if (status >= 400 && status < 500) {
            const message = err instanceof Error ? err.message : 'Refused';
            failed.push(q.kind === 'photo' ? `A photo was refused: ${message}` : `${q.label}: ${message}`);
            await drop(q.id);
          } else break;
        }
      }
    } finally { flushing = null; }
    return { sent, failed };
  })();
  return flushing;
}

/** The plug-in test (Warren, 21 October 2026): start with the cable unplugged, plug into a car, check after a minute. */
export const plugInTest = (jobId: string, identity: string, connectorId: number, step: 'start' | 'check') =>
  api.post(`admin/partner-jobs/${jobId}/site/plug-in-test`, { json: { identity, connector_id: connectorId, step }, timeout: 30_000, retry: 0 })
    .json<ApiResponse<{ passed?: boolean; message: string; watching_since?: string; check_after?: string }>>().then(r => r.data!);
/** The label test: the ID typed from the unit's label opens five minutes to charge from it in the EVI app. */
export const labelTest = (jobId: string, identity: string, connectorId: number, label: string) =>
  api.post(`admin/partner-jobs/${jobId}/site/label-test`, { json: { identity, connector_id: connectorId, label }, timeout: 30_000, retry: 0 })
    .json<ApiResponse<{ open_until: string; app_url: string | null; message: string }>>().then(r => r.data!);
/** "No signal": the crew marks a charger installed, offline, and leaves. Queued on the phone first: there is no signal. */
export const markInstalledOffline = (jobId: string, identity: string) => queueAction(jobId, `admin/partner-jobs/${jobId}/site/offline`, { identity }, `Installed, offline: ${identity}`);
export const finalTest = (jobId: string, identity: string) =>
  api.post(`admin/partner-jobs/${jobId}/site/final-test`, { json: { identity }, timeout: 60_000, retry: 0 }).json<ApiResponse<{ passed: boolean; problems: string[]; at: string }>>().then(r => r.data!);
export async function scan(jobId: string, slot: string, text: string) {
  try {
    return (await api.post(`admin/partner-jobs/${jobId}/site/scan`, { json: { slot, text }, timeout: 20_000, retry: 0 }).json<ApiResponse<{ matched: boolean; message: string }>>()).data!;
  } catch (err) {
    if ((err as { response?: Response }).response) throw err;
    await queueAction(jobId, `admin/partner-jobs/${jobId}/site/scan`, { slot, text }, 'A scan');
    return { matched: false, message: 'Saved on this phone. It is checked against this slot when you are back online.', queued: true };
  }
}

/** Keeps the job-site page itself on the phone for a reload without signal (public/sw.js serves it only then). */
export async function keepPageOffline() {
  if (!('caches' in window)) return;
  try {
    const cache = await caches.open('evi-admin-jobsite-v1');
    const assets = [...document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>('script[src], link[rel="stylesheet"][href], link[rel="modulepreload"][href]')]
      .map(el => (el as HTMLScriptElement).src || (el as HTMLLinkElement).href).filter(u => new URL(u).origin === location.origin && new URL(u).pathname.startsWith('/assets/'));
    const keep = new Set([...assets, `${location.origin}/__jobsite_shell`]);
    for (const req of await cache.keys()) if (!keep.has(req.url)) await cache.delete(req);
    const shell = await fetch(location.pathname, { credentials: 'same-origin' });
    if (shell.ok) await cache.put('/__jobsite_shell', shell);
    await Promise.all(assets.map(async a => { if (!await cache.match(a)) await cache.add(a).catch(() => undefined); }));
  } catch { /* offline reload is a convenience; the queue works without it */ }
}
